import { RefObject, useEffect } from 'react';

/**
 * Shared Escape/outside-click dismissal for the header's disclosures (Administreerimine panel,
 * account menu, mobile drawer) — see the spec's "only one panel or drawer is open at a time /
 * Escape closes it / a click outside closes it" behaviour. `containerRef` should wrap both the
 * trigger and the panel, so clicking the trigger itself (which already toggles `active` via its
 * own `onClick`) is never also treated as an "outside" click.
 *
 * `triggerRef`, when given, is focused on Escape whenever focus was inside `containerRef` at the
 * time (i.e. the trigger itself, or something the user tabbed to inside the open panel/drawer) —
 * done synchronously, before `onClose` runs and (for these disclosures) unmounts the panel, so
 * focus never has a chance to fall back to `<body>` in between.
 */
export function useDismissable(
  active: boolean,
  containerRef: RefObject<HTMLElement>,
  onClose: () => void,
  triggerRef?: RefObject<HTMLElement>
): void {
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') {
        return;
      }

      const container = containerRef.current;
      const trigger = triggerRef?.current;
      if (trigger && container && container.contains(document.activeElement)) {
        trigger.focus();
      }
      onClose();
    }

    function handlePointerDown(event: MouseEvent) {
      const container = containerRef.current;
      if (container && !container.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- containerRef/triggerRef are stable ref objects.
  }, [active, onClose]);
}

export default useDismissable;
